import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronRight, ArrowLeft, Receipt } from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";

const plans = [
  { id: "starter", name: "Starter", price: 19, features: ["20 quotes per month", "Estimate generator", "Copy text output", "Basic templates"] },
  { id: "pro",     name: "Pro",     price: 49, badge: "Best Value", features: ["Unlimited quotes", "PDF export", "Saved company info", "Saved clients", "Estimate, invoice & change order modes"] },
  { id: "crew",    name: "Crew",    price: 99, features: ["Everything in Pro", "Multiple users", "Saved trade templates", "Custom disclaimers", "Priority support"] },
];

export default function PricingPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-slate-500 hover:text-slate-900 mb-8 text-sm"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link>
        <h1 className="font-display text-4xl font-bold text-slate-900 text-center">Pricing</h1>
        <p className="text-slate-600 text-center mt-3">Simple plans. Cancel anytime. Or pay $5 per quote.</p>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-10">
          {plans.map((p) => {
            const isPro = p.name === "Pro";
            return (
              <Card key={p.name} className={`p-7 border shadow-card relative ${isPro ? "border-orange-300 ring-2 ring-orange-200" : "border-slate-200"}`}>
                {p.badge && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 hover:bg-orange-500 text-white">{p.badge}</Badge>}
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-slate-900">${p.price}</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/auth?mode=signup">
                  <Button
                    onClick={(e) => {
                      if (user) {
                        e.preventDefault();
                        startCheckout(p.id);
                      }
                    }}
                    data-testid={`pricing-cta-${p.id}`}
                    className={`mt-7 w-full ${isPro ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                  >
                    Get started <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </Card>
            );
          })}
        </div>

        {/* Pay per quote */}
        <Card className="mt-8 max-w-5xl mx-auto p-6 border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">Pay per quote</div>
              <div className="text-sm text-slate-500">No subscription. Pay $5 for a single professional quote.</div>
            </div>
          </div>
          <Link to="/auth?mode=signup">
            <Button
              onClick={(e) => {
                if (user) {
                  e.preventDefault();
                  startCheckout("per_quote");
                }
              }}
              variant="outline"
              data-testid="pricing-cta-per-quote"
            >
              Buy a quote — $5
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
